import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { http } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { LogoMark } from "@/components/lumi/Logo";

const Shell = ({ testid, title, children }) => (
  <main className="min-h-screen pt-[120px] pb-24 bg-[#F9ECE8] grain relative grid place-items-center px-5" data-testid={testid}>
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative max-w-xl text-center">
      <LogoMark className="w-14 h-14 mx-auto" />
      <h1 className="font-display text-5xl sm:text-6xl mt-8 leading-[0.95]">{title}</h1>
      <div className="mt-6 text-[#6E504A] leading-relaxed">{children}</div>
      <Link to="/" data-testid="result-home-link" className="inline-flex mt-10 rounded-full bg-[#281715] text-[#FAF7F2] px-8 h-12 items-center text-sm hover:bg-[#4A2E2B] transition-colors duration-200">Back to LUMI</Link>
    </motion.div>
  </main>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(sessionId ? "checking" : "error");
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) return undefined;
    let tries = 0;
    let timer;
    let stopped = false;
    const poll = async () => {
      try {
        const { data } = await http.get(`/payments/status/${sessionId}`);
        if (stopped) return;
        if (data?.payment_status === "paid") { setStatus("paid"); clear(); return; }
        if (data?.status === "expired") { setStatus("error"); return; }
      } catch { /* retry */ }
      tries += 1;
      if (tries >= 10) setStatus("timeout");
      else timer = setTimeout(poll, 2000);
    };
    poll();
    return () => { stopped = true; clearTimeout(timer); };
  }, [sessionId, clear]);

  if (status === "checking")
    return <Shell testid="payment-checking" title="Confirming your order…"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></Shell>;
  if (status === "paid")
    return <Shell testid="payment-success" title={<>Thank you. <span className="italic text-[#D88B94]">You're glowing.</span></>}>Your order is confirmed and a receipt is on its way to your inbox. We'll pack it in pink, cream and mocha with care.</Shell>;
  if (status === "timeout")
    return <Shell testid="payment-pending" title="Still confirming">We haven't received confirmation from the payment provider yet. Check your email for a receipt shortly.</Shell>;
  return <Shell testid="payment-error" title="We couldn't confirm that payment">If you were charged, check your email for a receipt. Otherwise, your bag is saved — try again anytime.</Shell>;
}

export function PaymentCancel() {
  return <Shell testid="payment-cancel" title={<>No rush. <span className="italic text-[#D88B94]">Your bag is saved.</span></>}>Checkout was cancelled and nothing was charged. Your items are waiting whenever you're ready.</Shell>;
}
